import { HeartPulse, HandHeart, GraduationCap, Stethoscope, Building2 } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { FadeUp } from "@/components/motion";
import { L } from "@/components/L";

const ICONS = [HeartPulse, HandHeart, GraduationCap, Stethoscope, Building2];

const ImageCard = ({ img, alt, title, text, Icon, className, testId }) => (
  <FadeUp className={`relative overflow-hidden img-zoom min-h-[340px] ${className}`} data-testid={testId}>
    <img src={img} alt={alt} loading="lazy" className="photo absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(12,30,21,0.88)] via-[rgba(12,30,21,0.35)] to-transparent" />
    <div className="relative h-full flex flex-col justify-end p-7 sm:p-8 text-white">
      <span className="h-11 w-11 grid place-items-center rounded-full bg-[var(--yellow)] text-[var(--ink)]"><Icon size={19} /></span>
      <h3 className="mt-5 text-2xl sm:text-3xl leading-tight">{title}</h3>
      <p className="mt-3 text-white/90 leading-relaxed max-w-lg text-[15px]">{text}</p>
    </div>
  </FadeUp>
);

const ToneCard = ({ title, text, Icon, tone, className, delay, testId, children }) => (
  <FadeUp delay={delay} className={`flex flex-col p-7 sm:p-8 ${tone} ${className}`} data-testid={testId}>
    <span className="h-11 w-11 grid place-items-center rounded-full bg-white text-[var(--green-deep)]"><Icon size={19} /></span>
    <h3 className="mt-5 text-2xl leading-tight">{title}</h3>
    <p className="mt-3 text-[var(--ink-soft)] leading-relaxed text-[15px]">{text}</p>
    {children}
  </FadeUp>
);

export const ActivitiesBento = ({ withLink = true }) => {
  const { t } = useLang();
  const items = t.activities.items;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[minmax(250px,auto)]" data-testid="activities-bento">
      <ImageCard img={IMG.childCap} alt={t.gallery.captions.childCap} title={items[0][0]} text={items[0][1]} Icon={ICONS[0]} className="md:col-span-2 lg:row-span-2 rounded-[2.5rem_2.5rem_2.5rem_0.5rem]" testId="activity-card-0" />
      <ToneCard title={items[1][0]} text={items[1][1]} Icon={ICONS[1]} tone="bg-[var(--yellow-pale)]" className="rounded-[2rem_2rem_0.5rem_2rem]" delay={0.05} testId="activity-card-1" />
      <ToneCard title={items[2][0]} text={items[2][1]} Icon={ICONS[2]} tone="bg-[var(--blue-pale)]" className="rounded-[2rem_0.5rem_2rem_2rem]" delay={0.1} testId="activity-card-2" />
      <ImageCard img={IMG.doctors} alt={t.gallery.captions.doctors} title={items[3][0]} text={items[3][1]} Icon={ICONS[3]} className="md:col-span-2 rounded-[2rem]" testId="activity-card-3" />
      <ToneCard title={items[4][0]} text={items[4][1]} Icon={ICONS[4]} tone="bg-[var(--green-pale)]" className="md:col-span-2 lg:col-span-4 rounded-[2rem_2rem_2rem_0.5rem] lg:flex-row lg:items-center lg:gap-10" delay={0.1} testId="activity-card-4">
        <div className="mt-6 lg:mt-0 lg:ml-auto flex flex-wrap gap-2">
          {t.activities.structures.map((s) => <span key={s} className="px-4 py-2 rounded-full bg-white text-[var(--green-deep)] text-sm font-semibold">{s}</span>)}
          {withLink && <L to="activities" className="px-4 py-2 rounded-full bg-[var(--green-deep)] text-white text-sm font-semibold hover:bg-[var(--green-night)] transition-colors" data-testid="activities-more-link">{t.common.discover} →</L>}
        </div>
      </ToneCard>
    </div>
  );
};
